import React, { useState, useEffect, lazy, Suspense, startTransition } from 'react';
import { useLocation } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { useDataCache } from '../contexts/DataCacheContext';
import configService from '../services/configService';

const VoiceSelection = lazy(() => import('./VoiceSelection'));
const BusinessDescription = lazy(() => import('./BusinessDescription'));

function Assistant() {
  const location = useLocation();
  const { cache, prefetchSettings } = useDataCache();
  const [loading, setLoading] = useState(!cache.settings);
  const [businessConfig, setBusinessConfig] = useState(cache.settings?.businessConfig || null);
  const [activeTab, setActiveTab] = useState('knowledge');
  const [isMobile, setIsMobile] = useState(false);
  const [expandedSection, setExpandedSection] = useState('knowledge');

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/voice')) setActiveTab('voice');
    else if (path === '/assistant') setActiveTab(prev => prev || 'knowledge');
  }, [location.pathname]);

  useEffect(() => {
    if (cache.settings) {
      setBusinessConfig(cache.settings.businessConfig);
      setLoading(false);
    } else {
      prefetchSettings();
    }
  }, [cache.settings]);

  const loadBusinessConfig = async () => {
    try {
      const config = await configService.getBusinessConfigByUserId(configService.getUserId());
      setBusinessConfig(config);
    } catch (error) {
      console.error('Failed to load business config:', error);
    }
  };

  const getCurrentSubPage = () => {
    const subPages = {
      'knowledge': { icon: 'fa-robot', label: 'Knowledge & Behavior' },
      'voice': { icon: 'fa-microphone', label: 'Voice Settings' }
    };
    return subPages[activeTab] || subPages['knowledge'];
  };

  return (
    <DashboardLayout currentSubPage={getCurrentSubPage()}>
      <div className="flex flex-1 flex-col md:flex-row">
        <div className="w-full md:w-64 md:flex-shrink-0 md:mr-4">
          <div className="h-full p-2" style={{background: '#F5F5F7'}}>
            <nav className="space-y-1 flex flex-col md:flex-col overflow-x-visible">
              
              <div>
                <button
                  onClick={() => {
                    startTransition(() => {
                      if (isMobile) {
                        setExpandedSection(expandedSection === 'knowledge' ? null : 'knowledge');
                      } else {
                        setActiveTab('knowledge');
                      }
                    });
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded transition-colors ${
                    (isMobile ? expandedSection === 'knowledge' : activeTab === 'knowledge')
                      ? 'text-white bg-[#3B3C42]'
                      : 'text-gray-700 hover:bg-[#2A2B2F] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <i className={`fa fa-robot text-sm`} style={(isMobile ? expandedSection === 'knowledge' : activeTab === 'knowledge') ? {color: '#3395FF'} : {}}></i>
                    <span className="font-medium text-sm">Knowledge & Behavior</span>
                  </div>
                  <i className={`fa fa-chevron-${expandedSection === 'knowledge' ? 'down' : 'right'} text-xs md:hidden`}></i>
                </button>
                {expandedSection === 'knowledge' && isMobile && (
                  <div className="mt-2 md:hidden">
                    {loading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="mb-6">
                          <div className="text-4xl font-bold text-gray-900 mb-2">AI Knowledge and Behavior</div>
                          <p className="text-sm text-gray-600">Configure what your AI assistant knows about your business and how it behaves.</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm border">
                          <BusinessDescription onComplete={loadBusinessConfig} cachedBusinessConfig={businessConfig} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <button
                  onClick={() => {
                    startTransition(() => {
                      if (isMobile) {
                        setExpandedSection(expandedSection === 'voice' ? null : 'voice');
                      } else {
                        setActiveTab('voice');
                      }
                    });
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded transition-colors ${
                    (isMobile ? expandedSection === 'voice' : activeTab === 'voice')
                      ? 'text-white bg-[#3B3C42]'
                      : 'text-gray-700 hover:bg-[#2A2B2F] hover:text-white'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <i className={`fa fa-microphone text-sm`} style={(isMobile ? expandedSection === 'voice' : activeTab === 'voice') ? {color: '#3395FF'} : {}}></i>
                    <span className="font-medium text-sm">Voice Settings</span>
                  </div>
                  <i className={`fa fa-chevron-${expandedSection === 'voice' ? 'down' : 'right'} text-xs md:hidden`}></i>
                </button>
                {expandedSection === 'voice' && isMobile && (
                  <div className="mt-2 md:hidden">
                    {loading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <div className="mb-6">
                          <div className="text-4xl font-bold text-gray-900 mb-2">Voice & Personality Settings</div>
                          <p className="text-sm text-gray-600">Configure your AI assistant's voice and personality traits.</p>
                        </div>
                        <div className="bg-white p-8 rounded-2xl shadow-sm border">
                          <VoiceSelection 
                            phoneNumber={businessConfig?.phone_number}
                            currentVoiceSettings={businessConfig?.voice_settings}
                            onVoiceSelected={loadBusinessConfig}
                            cachedVoices={cache.settings?.voices}
                            cachedBusinessConfig={businessConfig}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

            </nav>
          </div>
        </div>

        <div className="hidden md:block flex-1 min-w-0">
          <Suspense fallback={
            <div className="flex items-center justify-center h-64">
              <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
          }>
            {activeTab === 'knowledge' && (
              <div className="space-y-6">
                <div className="mb-6">
                  <div className="text-4xl font-bold text-gray-900 mb-2">AI Knowledge and Behavior</div>
                  <p className="text-sm text-gray-600">Configure what your AI assistant knows about your business and how it behaves.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-sm border">
                  <BusinessDescription onComplete={loadBusinessConfig} cachedBusinessConfig={businessConfig} />
                </div>
              </div>
            )}

            {activeTab === 'voice' && (
              <div className="space-y-6">
                <div className="mb-6">
                  <div className="text-4xl font-bold text-gray-900 mb-2">Voice & Personality Settings</div>
                  <p className="text-sm text-gray-600">Configure your AI assistant's voice and personality traits.</p>
                </div>
                <div className="bg-white p-8 rounded-2xl shadow-sm border">
                  <VoiceSelection 
                    phoneNumber={businessConfig?.phone_number}
                    currentVoiceSettings={businessConfig?.voice_settings}
                    onVoiceSelected={loadBusinessConfig}
                    cachedVoices={cache.settings?.voices}
                    cachedBusinessConfig={businessConfig}
                  />
                </div>
              </div>
            )}
          </Suspense>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default Assistant;
